import {
    Box,
    Typography,
    TextField,
    Button,
    useTheme,
    useMediaQuery,
    CircularProgress,
    Divider,
    InputAdornment,
    IconButton,
} from "@mui/material";
import React, { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { Send, Visibility, VisibilityOff } from "@mui/icons-material";
import { GoogleLogin } from "@react-oauth/google";
import { useNavigate } from "react-router-dom";
import useNotification from "./common/Notification";

const Login = () => {
    const [btnLoading, setBtnLoading] = useState(false);
    const [googleLoading, setGoogleLoading] = useState(false);
    const navigate = useNavigate();
    const notify = useNotification();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    const [password, setPassword] = useState("");
    const [email, setEmail] = useState("");
    const [showPassword, setShowPassword] = useState(false);

    const { login, loginWithGoogle } = useAuth();

    const handleLogin = async () => {
        try {
            setBtnLoading(true);
            await login({ email: email, password: password });
            navigate("/dashboard");
            notify("Login Successful!", "success");
        } catch (e) {
            if (e.response?.data?.error === "Account is disabled" ||
                e.response?.data?.message?.includes("disabled")) {
                notify(
                    e.response?.data?.message ||
                    "Your account has been disabled. Please contact support.",
                    "error"
                );
            } else {
                const errorMessage =
                    e.response?.data?.message ||
                    e.response?.data?.error ||
                    "Invalid username/ password.";
                notify(errorMessage, "error");
            }
        } finally {
            setBtnLoading(false);
        }
    };

    const handleGoogleSuccess = async (response) => {
        try {
            setGoogleLoading(true);
            await loginWithGoogle(response.credential);
            navigate("/dashboard");
            notify("Login Successful!", "success");
        } catch (e) {
            if (e.response?.data?.error === "Account is disabled" ||
                e.response?.data?.message?.includes("disabled")) {
                notify(
                    e.response?.data?.message ||
                    "Your account has been disabled. Please contact support.",
                    "error"
                );
            } else {
                const errorMessage =
                    e.response?.data?.message ||
                    e.response?.data?.error ||
                    "Google login failed. Please try again.";
                notify(errorMessage, "error");
            }
        } finally {
            setGoogleLoading(false);
        }
    };

    const handleGoogleError = () => {
        notify("Google login was cancelled or failed.", "error");
    };

    const handleChange = (field, e) => {
        if (field === "password") {
            setPassword(e.target.value);
        }
        if (field === "email") {
            setEmail(e.target.value);
        }
    };

    return (
        <>
            <Box
                sx={{
                    display: "flex",
                    height: "100vh",
                    width: "100vw",
                    overflow: "hidden",
                    flexDirection: { xs: "column", md: "row" },
                }}
            >
                {!isMobile && (
                    <Box
                        sx={{
                            width: "60%",
                            backgroundImage: 'url("/login-image-poster3.png")',
                            backgroundSize: "cover",
                            backgroundPosition: "center",
                        }}
                    />
                )}

                <Box
                    sx={{
                        position: "relative",
                        width: { xs: "100%", md: "40%" },
                        height: { xs: "100vh", md: "auto" },
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        overflow: "hidden",
                    }}
                >
                    <Box
                        sx={{
                            display: { xs: "block", md: "none" },
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            backgroundImage: 'url("/random-mobile-bg.png")',
                            backgroundSize: "contain",
                            backgroundPosition: "center",
                            zIndex: 0,
                        }}
                    />

                    <Box
                        sx={{
                            position: "relative",
                            zIndex: 1,
                            width: "80%",
                            marginBottom: { xs: "auto" },
                            marginTop: { xs: "3rem" },
                            maxWidth: 400,
                            bgcolor: "rgba(255, 255, 255, 0.62)",
                            backdropFilter: "blur(10px)",
                            WebkitBackdropFilter: "blur(10px)",
                            borderColor: "#bec2c2",
                            borderRadius: 2,
                            p: 2,
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "center",
                                mb: 3,
                            }}
                        >
                            <Box
                                component="img"
                                src="/images/app-logo.png"
                                alt="Varga Works"
                                sx={{
                                    width: "100%",
                                    maxWidth: 260,
                                    height: "auto",
                                    display: "block",
                                    boxSizing: "border-box",
                                    px: 2,
                                    py: 1.5,
                                    borderRadius: 1.5,
                                    bgcolor: "#10696e",
                                }}
                            />
                        </Box>
                        <Typography
                            variant="h4"
                            fontWeight="bold"
                            textAlign="center"
                            mb={2}
                            color="#10696e"
                        >
                            Welcome Back to Smart Outreach.
                        </Typography>
                        <Divider sx={{ mb: 4 }} />

                        <Typography
                            variant="h6"
                            fontWeight="bold"
                            textAlign="center"
                            mb={4}
                            color="#10696e"
                        >
                            Ready . Set . Broadcast
                        </Typography>

                        <Box component="form" noValidate autoComplete="off">
                            <TextField
                                fullWidth
                                size="small"
                                variant="outlined"
                                required
                                label="Email"
                                type="email"
                                sx={{
                                    mb: 2,
                                    "& .MuiOutlinedInput-root": {
                                        "&.Mui-focused fieldset": {
                                            borderColor: "#1c8b91",
                                        },
                                    },
                                    "& label.Mui-focused": {
                                        color: "#1c8b91",
                                    },
                                }}
                                onChange={(e) => handleChange("email", e)}
                                value={email}
                            />

                            <TextField
                                fullWidth
                                variant="outlined"
                                size="small"
                                label="Password"
                                required
                                type={showPassword ? "text" : "password"}
                                sx={{
                                    mb: 3,
                                    "& .MuiOutlinedInput-root": {
                                        "&.Mui-focused fieldset": {
                                            borderColor: "#1c8b91",
                                        },
                                    },
                                    "& label.Mui-focused": {
                                        color: "#1c8b91",
                                    },
                                }}
                                onChange={(e) => handleChange("password", e)}
                                value={password}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                aria-label="toggle password visibility"
                                                onClick={() => setShowPassword(!showPassword)}
                                                edge="end"
                                                sx={{
                                                    color: "#1c8b91",
                                                    "&:hover": {
                                                        backgroundColor: "rgba(28, 139, 145, 0.1)",
                                                    },
                                                }}
                                            >
                                                {showPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />

                            <Button
                                fullWidth
                                variant="contained"
                                size="small"
                                endIcon={
                                    btnLoading ? (
                                        <CircularProgress
                                            size={16}
                                            color="inherit"
                                        />
                                    ) : (
                                        <Send />
                                    )
                                }
                                disabled={btnLoading || googleLoading}
                                color="primary"
                                sx={{
                                    backgroundColor: "#10696e",
                                    color: "#fff",
                                    textTransform: "none",
                                    "&:hover": {
                                        opacity: "0.9",
                                        backgroundColor: "#10696e",
                                    },
                                }}
                                onClick={handleLogin}
                            >
                                Login
                            </Button>

                            <Divider sx={{ my: 2.5 }}>
                                <Typography variant="caption" color="text.secondary">
                                    OR
                                </Typography>
                            </Divider>

                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "center",
                                    opacity: googleLoading ? 0.6 : 1,
                                    pointerEvents: googleLoading ? "none" : "auto",
                                }}
                            >
                                {googleLoading ? (
                                    <CircularProgress size={28} sx={{ color: "#10696e" }} />
                                ) : (
                                    <GoogleLogin
                                        onSuccess={handleGoogleSuccess}
                                        onError={handleGoogleError}
                                        theme="outline"
                                        size="large"
                                        width="320"
                                        text="continue_with"
                                        shape="rectangular"
                                    />
                                )}
                            </Box>
                        </Box>
                        <Typography
                            sx={{ mt: 4, textAlign: "center" }}
                            style={{
                                fontFamily: "Poppins, sans-serif",
                                fontSize: "14px",
                                color: "#888",
                                fontStyle: "italic",
                            }}
                        >
                            Powered by
                            <a
                                href="https://www.vibhumsoftware.com"
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ display: "inline-block", verticalAlign: "middle", marginLeft: "4px" }}
                            >
                                <span
                                    style={{
                                        display: "inline-block",
                                        width: "80px",
                                        height: "22px",
                                        backgroundImage:
                                            'url("https://vibhumsoftware.com/wp-content/uploads/2025/06/vibhum-logo.png")',
                                        backgroundSize: "contain",
                                        backgroundRepeat: "no-repeat",
                                        backgroundPosition: "center",
                                    }}
                                ></span>
                            </a>
                        </Typography>
                    </Box>
                </Box>
            </Box>
        </>
    );
};

export default Login;
